import React, { useState, useEffect } from "react";
import { UserAccount, LoginBanner, LoginPageFooter } from '../types';
import { initiateZohoSSO } from '../services/zoho';
import { authApi } from '../services/api';
import api from '../services/api';
import MagicRings from './MagicRings';

interface LoginProps {
  onLogin: (user: UserAccount, token?: string) => void;
  onSSOLogin: (user: UserAccount, token?: string) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin, onSSOLogin }) => {
  const [adminUser, setAdminUser] = useState("");
  const [adminPass, setAdminPass] = useState("");
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [banners, setBanners] = useState<LoginBanner[]>([]);
  const [bannerIndex, setBannerIndex] = useState(0);
  const [footer, setFooter] = useState<LoginPageFooter | null>(null);
  const [mounted, setMounted] = useState(false);
  const [ssoError, setSsoError] = useState<string>('');

  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    if (!error) return;
    try {
      setSsoError(decodeURIComponent(error));
    } catch {
      setSsoError(error);
    }
  }, []);

  useEffect(() => {
    api.get('/public/login-page').then((res) => {
      const list = res.data?.banners ?? [];
      setBanners(Array.isArray(list) ? list : []);
      setFooter(res.data?.footer ?? null);
    }).catch(() => {
      setBanners([]);
      setFooter(null);
    });
  }, []);

  useEffect(() => {
    if (banners.length <= 1) return;
    const t = setInterval(() => {
      setBannerIndex((i) => (i + 1) % banners.length);
    }, 6000);
    return () => clearInterval(t);
  }, [banners.length]);

  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsAuthenticating(true);
    try {
      const res = await authApi.loginAdmin({
        email: adminUser,
        password: adminPass,
      });
      const token = res.tokens?.accessToken;
      if (token) {
        localStorage.setItem("ma_jwt", token);
      }
      onLogin(res.user, token);
    } catch (err: any) {
      alert(err?.response?.data?.error || "Login failed");
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleZohoSSO = () => {
    setIsAuthenticating(true);
    initiateZohoSSO(); // redirects to backend for real OAuth
  };

  const showFooter = footer?.visible !== false;
  const footerLeft = footer?.leftText ?? 'Miracle Experience — Safari Intelligence';
  const footerRight = footer?.rightText ?? 'Secure access · Enterprise node';
  const footerImageUrl = footer?.imageUrl;
  const footerAnimated = footer?.animated === true;
  // Responsive height: admin value clamped 60–400; we cap max for large screens and use min for small
  const footerImageHeightPx = Math.min(400, Math.max(60, footer?.imageHeightPx ?? 140));

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col overflow-hidden"
      style={{
        background: 'linear-gradient(160deg, #1a1206 0%, #221a07 40%, #181206 70%, #0d0a03 100%)',
        height: '100vh',
        minHeight: '100vh',
        maxHeight: '100vh',
        opacity: 0,
        animation: mounted ? 'login-page-enter 0.5s ease-out forwards' : undefined,
      }}
    >
      <style>{`
        @keyframes scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        @keyframes gradient-shift {
          0%, 100% {
            opacity: 0.3;
          }
          50% {
            opacity: 0.5;
          }
        }
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        @keyframes float-reverse {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(20px);
          }
        }
        @keyframes pulse-dots {
          0% {
            opacity: 1;
          }
          50% {
            opacity: 0.4;
          }
          100% {
            opacity: 1;
          }
        }
        @keyframes logo-glow-pulse {
          0%, 100% {
            box-shadow: 0 0 20px rgba(79, 70, 229, 0.4), 
                        0 0 40px rgba(79, 70, 229, 0.2),
                        0 0 60px rgba(6, 182, 212, 0.1),
                        inset 0 0 20px rgba(79, 70, 229, 0.2);
          }
          50% {
            box-shadow: 0 0 30px rgba(79, 70, 229, 0.6),
                        0 0 60px rgba(79, 70, 229, 0.3),
                        0 0 80px rgba(6, 182, 212, 0.2),
                        inset 0 0 30px rgba(79, 70, 229, 0.3);
          }
        }
        @keyframes logo-rotate-gradient {
          0% {
            background: linear-gradient(135deg, #d4af37 0%, #f4d03f 50%, #f9e4b7 100%);
          }
          50% {
            background: linear-gradient(225deg, #f4d03f 0%, #c5a028 50%, #d4af37 100%);
          }
          100% {
            background: linear-gradient(135deg, #d4af37 0%, #f4d03f 50%, #f9e4b7 100%);
          }
        }
        @keyframes logo-inner-glow {
          0%, 100% {
            box-shadow: inset 0 0 20px rgba(212, 175, 55, 0.3);
          }
          50% {
            box-shadow: inset 0 0 30px rgba(244, 208, 63, 0.5);
          }
        }
        @keyframes logo-shine {
          0% {
            transform: translateX(-100%) translateY(-100%) rotate(45deg);
          }
          100% {
            transform: translateX(100%) translateY(100%) rotate(45deg);
          }
        }
        @keyframes logo-sparkle {
          0%, 100% {
            opacity: 0;
            transform: scale(0.5);
          }
          50% {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes logo-inner-glow {
          0%, 100% {
            filter: brightness(1) drop-shadow(0 0 10px rgba(255, 255, 255, 0.3));
          }
          50% {
            filter: brightness(1.1) drop-shadow(0 0 20px rgba(255, 255, 255, 0.5));
          }
        }
        @keyframes footer-pan {
          0% { transform: scale(1.05) translate(0, 0); }
          100% { transform: scale(1.05) translate(-1.5%, -1%); }
        }
        @keyframes border-rotate {
          0% { --angle: 0deg; }
          100% { --angle: 360deg; }
        }
        @property --angle {
          syntax: '<angle>';
          initial-value: 0deg;
          inherits: false;
        }
        @keyframes slide-progress {
          0% { transform: scaleX(0); }
          100% { transform: scaleX(1); }
        }
        @keyframes ambient-pulse {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        @keyframes grain {
          0%, 100% { transform: translate(0, 0); }
          10% { transform: translate(-1%, -1%); }
          20% { transform: translate(1%, 0%); }
          30% { transform: translate(0%, 1%); }
          40% { transform: translate(-1%, 1%); }
          50% { transform: translate(1%, -1%); }
          60% { transform: translate(0%, 0%); }
          70% { transform: translate(-1%, 0%); }
          80% { transform: translate(1%, 1%); }
          90% { transform: translate(0%, -1%); }
        }
        @keyframes card-shimmer {
          0% { transform: translateX(-100%) rotate(15deg); }
          100% { transform: translateX(100%) rotate(15deg); }
        }
        @keyframes login-page-enter {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
        {/* Film grain texture overlay */}
        <div className="absolute inset-0 pointer-events-none z-[2] opacity-[0.04]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          animation: 'grain 0.5s steps(1) infinite',
        }} />
        {/* MagicRings WebGL background — full screen, amber/gold brand colours */}
        <div className="absolute inset-0 pointer-events-none z-0">
          <MagicRings
            color="#d4af37"
            colorTwo="#f59e0b"
            ringCount={7}
            speed={0.55}
            attenuation={9}
            lineThickness={2.2}
            baseRadius={0.28}
            radiusStep={0.11}
            scaleRate={0.12}
            opacity={0.82}
            blur={0}
            noiseAmount={0.04}
            rotation={0}
            ringGap={1.6}
            fadeIn={0.65}
            fadeOut={0.55}
            followMouse={true}
            mouseInfluence={0.06}
            hoverScale={1.05}
            parallax={0.02}
            clickBurst={false}
          />
        </div>
        {/* Dark vignette over rings so content stays legible */}
        <div className="absolute inset-0 pointer-events-none z-[1]" style={{
          background: 'radial-gradient(ellipse 100% 100% at 50% 50%, rgba(20,14,4,0.30) 0%, rgba(12,8,2,0.68) 60%, rgba(6,4,1,0.85) 100%)',
        }} />
        {/* ── 1. Status ticker ── */}
        <div className="w-full overflow-hidden py-2.5 shrink-0 relative z-10" style={{ background: 'linear-gradient(90deg, rgba(16,10,2,0.99) 0%, rgba(24,16,4,0.97) 50%, rgba(16,10,2,0.99) 100%)', borderBottom: '1px solid rgba(212,175,55,0.18)', boxShadow: '0 1px 0 rgba(212,175,55,0.06), 0 4px 20px rgba(0,0,0,0.4)' }}>
          <div className="flex gap-16 items-center" style={{ animation: 'scroll 30s linear infinite' }}>
            {[1, 2].map((i) => (
              <React.Fragment key={i}>
                <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/85 whitespace-nowrap flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  SERENGETI SECTOR | TEMP: 24°C | WIND: E/08KT | BALLOON OPS: ACTIVE
                </span>
                <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/85 whitespace-nowrap flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  ZANZIBAR ZONE | TEMP: 31°C | WIND: S/15KT | LAUNCH STATUS: READY
                </span>
                <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/85 whitespace-nowrap flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-sky-400 animate-pulse" />
                  BALLOON ME-101 ASCENDING | ALT: 8,500FT | WIND DRIFT: 8KT
                </span>
                <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/85 whitespace-nowrap flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  GLOBAL PLATFORM SYNCHRONIZED | POSTGRES CONNECTED | ZOHO HANDSHAKE READY
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ── 2. Main content (banners + login) ── */}
        <div className={`flex-1 flex min-h-0 w-full overflow-hidden relative z-10 ${banners.length > 0 ? 'flex-row' : 'items-center justify-center'}`}>
          {/* Banner carousel — left column on lg+ — framed like a premium display */}
          {banners.length > 0 && (
            <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] items-center justify-center p-6 xl:p-10">
              {/* Amber ambient backlight behind the banner frame */}
              <div className="absolute pointer-events-none" style={{
                width: '70%', height: '75%', left: '2%', top: '12%',
                background: 'radial-gradient(ellipse at center, rgba(212,175,55,0.15) 0%, rgba(180,130,20,0.07) 50%, transparent 75%)',
                filter: 'blur(50px)',
                animation: 'ambient-pulse 6s ease-in-out infinite',
              }} />

              {/* Animated gradient border wrapper */}
              <div className="relative w-full h-full rounded-[26px] p-[2px]" style={{
                background: `conic-gradient(from var(--angle, 0deg), rgba(212,175,55,0.6), rgba(79,70,229,0.4), rgba(6,182,212,0.5), rgba(212,175,55,0.1), rgba(79,70,229,0.4), rgba(212,175,55,0.6))`,
                animation: 'border-rotate 8s linear infinite',
              }}>
                {/* Outer frame with depth */}
                <div
                  className="relative w-full h-full rounded-3xl overflow-hidden"
                  style={{
                    boxShadow: '0 25px 60px rgba(0,0,0,0.6), 0 8px 20px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
                  }}
                >
                  {/* Inner highlight ring for depth */}
                  <div className="absolute inset-0 z-30 rounded-3xl pointer-events-none" style={{ boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)' }} />

                  {/* Slides */}
                {banners.map((b, i) => {
                  const active = i === bannerIndex;
                  const cls = `absolute inset-0 transition-opacity duration-1000 ease-in-out ${b.linkUrl ? 'cursor-pointer' : ''} ${active ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`;
                  const style = {
                    backgroundImage: b.imageUrl ? `url(${b.imageUrl})` : undefined,
                    backgroundSize: 'cover' as const,
                    backgroundPosition: 'center' as const,
                    backgroundRepeat: 'no-repeat' as const,
                  };
                  const inner = (
                    <>
                      <div className="absolute inset-0" style={{ backgroundColor: b.overlayColor || '#000000', opacity: b.overlayOpacity ?? 0.25 }} />
                      {(b.title || b.subtitle) && (
                        <div
                          className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center"
                          style={{ color: b.textColor || '#ffffff', fontFamily: b.fontFamily && b.fontFamily !== 'system' ? b.fontFamily : undefined }}
                        >
                          {b.title && <span className="font-bold max-w-xl drop-shadow-lg" style={{ fontSize: Math.min(32, (b.titleFontSize ?? 28)) }}>{b.title}</span>}
                          {b.subtitle && <span className="mt-2 opacity-90 max-w-md drop-shadow-md" style={{ fontSize: Math.min(16, (b.subtitleFontSize ?? 14)) }}>{b.subtitle}</span>}
                        </div>
                      )}
                    </>
                  );
                  return b.linkUrl ? (
                    <a key={b.id} href={b.linkUrl} target="_blank" rel="noopener noreferrer" className={cls} style={style}>{inner}</a>
                  ) : (
                    <div key={b.id} className={cls} style={style}>{inner}</div>
                  );
                })}

                {/* Carousel dots — sitting on a frosted shelf at the bottom */}
                {banners.length > 1 && (
                  <div className="absolute bottom-5 left-0 right-0 flex justify-center gap-2.5 z-20">
                    <div className="flex gap-2.5 bg-black/30 backdrop-blur-xl rounded-full px-4 py-2 border border-white/10">
                      {banners.map((_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setBannerIndex(i)}
                          className={`rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 focus-visible:ring-offset-black/50 ${
                            i === bannerIndex ? 'w-6 h-2.5 bg-amber-400 shadow-[0_0_8px_rgba(244,208,63,0.6)]' : 'w-2.5 h-2.5 bg-white/40 hover:bg-white/70'
                          }`}
                          aria-label={`Slide ${i + 1}`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Slide progress bar — top edge of frame */}
                {banners.length > 1 && (
                  <div className="absolute top-0 left-0 right-0 h-[3px] z-30 bg-black/20">
                    <div
                      key={bannerIndex}
                      className="h-full bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-300 origin-left"
                      style={{
                        animation: 'slide-progress 6s linear',
                        boxShadow: '0 0 12px rgba(244,208,63,0.6)',
                      }}
                    />
                  </div>
                )}

                {/* Corner accent marks */}
                <div className="absolute top-3 left-3 w-5 h-5 z-30 pointer-events-none border-t-2 border-l-2 border-amber-400/40 rounded-tl-lg" />
                <div className="absolute top-3 right-3 w-5 h-5 z-30 pointer-events-none border-t-2 border-r-2 border-amber-400/40 rounded-tr-lg" />
                <div className="absolute bottom-3 left-3 w-5 h-5 z-30 pointer-events-none border-b-2 border-l-2 border-amber-400/40 rounded-bl-lg" />
                <div className="absolute bottom-3 right-3 w-5 h-5 z-30 pointer-events-none border-b-2 border-r-2 border-amber-400/40 rounded-br-lg" />
              </div>
              </div>
            </div>
          )}

          {/* Login card area — right column or full-width */}
          <div className={`flex-1 flex items-center justify-center min-h-full w-full relative ${banners.length > 0 ? 'lg:pl-0' : ''}`}>
            {/* Right panel dark glass layer */}
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(20,14,4,0.50) 0%, rgba(26,18,5,0.60) 50%, rgba(12,8,2,0.68) 100%)' }} />
            {/* Amber halo centered behind card — card feels like it's emitting light */}
            <div className="absolute pointer-events-none" style={{
              width: '520px', height: '520px',
              top: '50%', left: '50%',
              transform: 'translate(-50%, -52%)',
              background: 'radial-gradient(ellipse at center, rgba(212,175,55,0.13) 0%, rgba(180,130,20,0.06) 35%, transparent 70%)',
              filter: 'blur(40px)',
              animation: 'ambient-pulse 7s ease-in-out infinite',
            }} />
            {/* Subtle dot grid */}
            <div className="absolute inset-0 pointer-events-none" style={{
              backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.09) 1px, transparent 1px)',
              backgroundSize: '32px 32px',
              maskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, black 20%, transparent 100%)',
              WebkitMaskImage: 'radial-gradient(ellipse 70% 70% at 50% 50%, black 20%, transparent 100%)',
            }} />

            <div className="relative w-full max-w-md px-6 py-6 md:py-8 z-10 overflow-y-auto max-h-full">
              {/* Main card */}
              <div className="relative backdrop-blur-2xl rounded-3xl border border-white/[0.14] p-8 md:p-10 overflow-hidden group" style={{ background: 'linear-gradient(160deg, rgba(34,24,7,0.93) 0%, rgba(24,17,4,0.97) 100%)', boxShadow: '0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(212,175,55,0.10), 0 -1px 0 0 rgba(212,175,55,0.28) inset, 0 0 120px rgba(212,175,55,0.12)' }}>
                {/* Amber top-border glow line */}
                <div className="absolute inset-x-0 top-0 h-px pointer-events-none" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(212,175,55,0.7) 30%, rgba(250,220,80,0.9) 50%, rgba(212,175,55,0.7) 70%, transparent 100%)' }} />
                {/* Top-left inner highlight */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/[0.04] via-transparent to-transparent rounded-3xl pointer-events-none" />
                {/* Shimmer sweep */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-3xl">
                  <div className="absolute -inset-full w-[200%] h-[200%] opacity-[0.06]" style={{ background: 'linear-gradient(115deg, transparent 40%, rgba(255,255,255,0.9) 50%, transparent 60%)', animation: 'card-shimmer 8s ease-in-out infinite' }} />
                </div>

                {/* Logo */}
                <div className="text-center mb-8 md:mb-10 relative z-10">
                  {/* Ambient glow sits behind the logo, outside the clipping box */}
                  <div className="absolute pointer-events-none" style={{ width: '340px', height: '180px', top: '50%', left: '50%', transform: 'translate(-50%, -54%)', background: 'radial-gradient(ellipse at 38% 55%, rgba(212,175,55,0.22) 0%, rgba(212,175,55,0.07) 50%, transparent 75%)', filter: 'blur(28px)' }} />
                  <div className="mx-auto mb-6 relative flex items-center justify-center" style={{ width: '320px', height: '159px' }}>
                    {/* Logo — exact SVG viewBox ratio 340:169 → 320:150 — brightness+contrast lifts cream text off the dark card */}
                    <img
                      src="/meos/images/me-logo.svg"
                      alt="Miracle Experience Logo"
                      className="w-full h-full object-contain"
                      style={{ filter: 'brightness(1.55) contrast(1.2) saturate(1.05) drop-shadow(0 4px 24px rgba(0,0,0,0.7))' }}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const fb = e.currentTarget.nextElementSibling as HTMLElement;
                        if (fb) fb.style.display = 'flex';
                      }}
                    />
                    {/* Fallback wordmark */}
                    <div className="absolute inset-0 hidden items-center justify-center flex-col gap-1" style={{ display: 'none' }}>
                      <span className="text-2xl font-black tracking-tight" style={{ backgroundImage: 'linear-gradient(135deg, #fde68a 0%, #f59e0b 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>MIRACLE</span>
                      <span className="text-xs font-bold tracking-[0.3em] text-amber-400/80 uppercase">Experience</span>
                    </div>
                  </div>

                  <h1 className="text-3xl md:text-4xl font-black tracking-tighter mb-3" style={{ backgroundImage: 'linear-gradient(135deg, #ffffff 0%, #fde68a 60%, #f59e0b 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>ME-OS</h1>
                  <p className="text-xs font-bold text-slate-300 uppercase tracking-[0.2em]">Safari Experience Hub</p>
                </div>

                {/* Auth */}
                <div className="space-y-6 relative z-10">
                  {ssoError ? (
                    <div className="rounded-xl border border-rose-400/60 bg-rose-500/10 px-4 py-3 text-sm font-semibold text-rose-200">
                      {ssoError}
                    </div>
                  ) : null}
                  {!showAdminPanel ? (
                    <div className="space-y-7 animate-in fade-in slide-in-from-bottom-4 duration-500">
                      <button onClick={handleZohoSSO} disabled={isAuthenticating} className="w-full py-4 px-6 bg-gradient-to-r from-amber-400 to-yellow-400 hover:from-amber-300 hover:to-yellow-300 rounded-xl flex items-center justify-center gap-4 transition-all duration-300 shadow-xl shadow-amber-400/60 hover:shadow-2xl hover:shadow-amber-400/80 hover:-translate-y-1 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed group border border-yellow-300/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900">
                        {isAuthenticating ? (
                          <div className="flex items-center gap-3"><div className="w-5 h-5 border-2 border-white/80 border-t-white rounded-full animate-spin" /><span className="text-sm font-semibold text-white">Verifying Corporate ID...</span></div>
                        ) : (
                          <>
                            <svg className="w-5 h-5 text-amber-900/80 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M7.864 4.243A7.5 7.5 0 0 1 19.5 10.5c0 2.92-.556 5.709-1.568 8.268M5.742 6.364A7.465 7.465 0 0 0 4.5 10.5a7.464 7.464 0 0 1-1.15 3.993m1.989 3.559A11.209 11.209 0 0 0 8.25 10.5a3.75 3.75 0 1 1 7.5 0c0 .527-.021 1.049-.064 1.565M12 10.5a14.94 14.94 0 0 1-3.6 9.75m6.633-4.596a18.666 18.666 0 0 1-2.485 5.33" />
                            </svg>
                            <span className="text-sm font-semibold text-amber-900 tracking-wide">Authenticate via Zoho People</span>
                          </>
                        )}
                      </button>
                      <div className="relative"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-600/40" /></div><div className="relative flex justify-center"><span className="px-4 text-xs font-bold text-slate-500 uppercase tracking-wider" style={{ backgroundColor: 'transparent' }}>or</span></div></div>
                      <div className="text-center">
                        <button onClick={() => setShowAdminPanel(true)} className="inline-flex items-center gap-2 text-xs font-bold text-slate-400 hover:text-indigo-300 transition-colors duration-300 tracking-wider uppercase focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded">
                          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                          System Override [Admin Only]
                        </button>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleAdminLogin} className="space-y-6 animate-in fade-in slide-in-from-top-4 duration-500">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label className="text-xs font-bold text-slate-300 tracking-widest uppercase block">Command Identity</label>
                          <input type="text" required placeholder="admin@miracleair.co.tz" className="w-full px-4 py-3.5 bg-slate-700/30 border border-slate-600/40 rounded-xl text-white text-sm placeholder:text-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 focus:bg-slate-700/50 focus-visible:ring-indigo-400/50 transition-all duration-300 backdrop-blur-sm" value={adminUser} onChange={(e) => setAdminUser(e.target.value)} />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-bold text-slate-300 tracking-widest uppercase block">Access Protocol</label>
                          <input type="password" required placeholder="Enter secure password" className="w-full px-4 py-3.5 bg-slate-700/30 border border-slate-600/40 rounded-xl text-white text-sm placeholder:text-slate-400 focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 focus:bg-slate-700/50 focus-visible:ring-indigo-400/50 transition-all duration-300 backdrop-blur-sm" value={adminPass} onChange={(e) => setAdminPass(e.target.value)} />
                        </div>
                      </div>
                      <div className="flex gap-3 pt-2">
                        <button type="button" onClick={() => setShowAdminPanel(false)} className="px-5 py-3.5 rounded-xl border border-slate-600/40 text-slate-300 hover:text-white hover:border-slate-500 hover:bg-slate-700/30 transition-all duration-300 text-sm font-bold uppercase tracking-wide focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900">Cancel</button>
                        <button type="submit" disabled={isAuthenticating} className="flex-1 py-3.5 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-400 text-white rounded-xl font-bold text-sm shadow-xl shadow-indigo-500/40 transition-all duration-300 flex items-center justify-center gap-2 hover:-translate-y-1 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed border border-indigo-400/50 uppercase tracking-wide focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900">
                          {isAuthenticating ? (<><div className="w-4 h-4 border-2 border-white/80 border-t-white rounded-full animate-spin" /><span>Authenticating...</span></>) : "Initiate Override"}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>

              {/* Micro-footer below card */}
              <div className="mt-6 text-center space-y-1.5">
                <div className="flex items-center justify-center gap-3">
                  <div className="h-px w-10 bg-gradient-to-r from-transparent to-slate-600/50" />
                  <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-[0.2em]">Safari Experience Node 2026-05 | v1.1.0-ENTERPRISE</p>
                  <div className="h-px w-10 bg-gradient-to-l from-transparent to-slate-600/50" />
                </div>
                <p className="text-[10px] text-slate-500/60 tracking-wider font-medium">Proprietary Operating System | Blink By GadgetroniX.</p>
              </div>
            </div>
          </div>
        </div>

        {/* ── 3. Corporate Card Footer ── */}
        {showFooter && (
          <footer className="w-full shrink-0 relative z-20 overflow-hidden" style={{ minHeight: footerImageUrl ? Math.max(120, footerImageHeightPx) : 120 }}>
            {/* Background: image or solid */}
            {footerImageUrl ? (
              <>
                <div
                  className="absolute inset-0 w-full h-full"
                  style={{
                    backgroundImage: `url(${footerImageUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    animation: footerAnimated ? 'footer-pan 40s ease-in-out infinite alternate' : undefined,
                  }}
                />
                <div className="absolute inset-0" style={{ background: 'rgba(12,8,2,0.50)' }} />
              </>
            ) : (
              <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, #1a1206 0%, #0d0903 100%)' }} />
            )}
            {/* Top border accent */}
            <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-amber-500/0 via-amber-400/80 to-amber-500/0 z-10" />
            
            {/* Cards Grid */}
            <div className="relative z-10 w-full h-full flex items-center justify-center px-6 sm:px-12 py-6 sm:py-8">
              <div className="w-full max-w-6xl grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                
                {/* Card 1 */}
                <div className="relative flex items-center gap-4 bg-white/[0.07] backdrop-blur-xl rounded-2xl px-6 py-5 border border-white/[0.12] shadow-lg hover:bg-white/[0.12] hover:border-amber-400/20 hover:shadow-amber-400/10 hover:shadow-xl transition-all duration-500 group overflow-hidden">
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                  <div className="w-11 h-11 rounded-xl bg-amber-400/20 border border-amber-400/30 flex items-center justify-center shrink-0 group-hover:scale-110 group-hover:shadow-[0_0_20px_rgba(212,175,55,0.3)] transition-all duration-300">
                    <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] sm:text-xs font-bold text-white uppercase tracking-wider truncate">{footerLeft}</p>
                    <p className="text-[10px] text-white/50 mt-0.5 font-medium">Secure Enterprise Platform</p>
                  </div>
                </div>

                {/* Card 2 */}
                <div className="relative flex items-center gap-4 bg-white/[0.07] backdrop-blur-xl rounded-2xl px-6 py-5 border border-white/[0.12] shadow-lg hover:bg-white/[0.12] hover:border-emerald-400/20 hover:shadow-emerald-400/10 hover:shadow-xl transition-all duration-500 group overflow-hidden">
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                  <div className="w-11 h-11 rounded-xl bg-emerald-400/20 border border-emerald-400/30 flex items-center justify-center shrink-0 group-hover:scale-110 group-hover:shadow-[0_0_20px_rgba(52,211,153,0.3)] transition-all duration-300">
                    <div className="relative">
                      <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                      <div className="absolute inset-0 w-2.5 h-2.5 rounded-full bg-emerald-400/50 animate-ping" />
                    </div>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] sm:text-xs font-bold text-white uppercase tracking-wider">All Systems Online</p>
                    <p className="text-[10px] text-emerald-400/80 mt-0.5 font-medium">Connected · Low Latency</p>
                  </div>
                </div>

                {/* Card 3 */}
                <div className="relative flex items-center gap-4 bg-white/[0.07] backdrop-blur-xl rounded-2xl px-6 py-5 border border-white/[0.12] shadow-lg hover:bg-white/[0.12] hover:border-sky-400/20 hover:shadow-sky-400/10 hover:shadow-xl transition-all duration-500 group overflow-hidden">
                  <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                  <div className="w-11 h-11 rounded-xl bg-sky-400/20 border border-sky-400/30 flex items-center justify-center shrink-0 group-hover:scale-110 group-hover:shadow-[0_0_20px_rgba(56,189,248,0.3)] transition-all duration-300">
                    <svg className="w-5 h-5 text-sky-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[11px] sm:text-xs font-bold text-white uppercase tracking-wider truncate">{footerRight}</p>
                    <p className="text-[10px] text-white/50 mt-0.5 font-medium">Global Safari Network</p>
                  </div>
                </div>

              </div>
            </div>
          </footer>
        )}
    </div>
  );
};

export default Login;
